import React, { useCallback, useContext, useEffect, useState } from 'react';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import {
  Box,
  Card,
  InputAdornment,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'oidc-react';
import { useMutation } from '@tanstack/react-query';
import {
  comboStyle,
  DEFAULT_BUSINESS_START_HOUR,
} from '../../constants';
import { SettingsContext } from '../../context/SettingsContext';
import { DayOfWeek } from '../../helpers/dateUtils';
import useSavedField from '../../helpers/useSavedField';
import { ApiFetchParams, useApiQuery } from '../../helpers/useApi';
import { apiFetch } from '../../api/fetch';
import EnhancedSnackbar, { Snackbar } from '../../components/EnhancedSnackbar';
import { defer, retryOperation } from '../../helpers/helpers';
import CalendarPreferences from '../../models/calendar';

const subheaderStyle = { color: 'text.secondary', fontSize: '16px', marginTop: '4px' };

const Preferences = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { settings, setSetting } = useContext(SettingsContext);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<Snackbar | undefined>(undefined);
  const [allowUpdate, setAllowUpdate] = useState(false);
  const maybeAccessToken = auth?.userData?.access_token;
  const theme = useTheme();

  useEffect(() => {
    const id = setTimeout(() => setAllowUpdate(true), 400);
    return () => clearTimeout(id);
  }, []);

  const { isLoading: isLoadingCalendarPreferences, data: calendarPreferences } =
    useApiQuery<CalendarPreferences>({
      endpoint: process.env.REACT_APP_API_V1,
      path: `org/organisation/${auth!.userData!.profile!.sub}/calendar`,
      body: {}
    } as ApiFetchParams, "calendar", { enabled: !!auth?.userData?.profile?.sub });

  const {
    val: daysStart,
    setVal: setDaysStart,
    changed: daysStartChanged,
    apply: applyDaysStart,
    forceSet: forceSetDaysStart,
  } = useSavedField(new Date(2024, 1, 1, DEFAULT_BUSINESS_START_HOUR));

  const {
    val: weeksStart,
    setVal: setWeeksStart,
    changed: weeksStartChanged,
    apply: applyWeeksStart,
    forceSet: forceSetWeeksStart,
  } = useSavedField("monday");

  const {
    val: rowsPerPage,
    setVal: setRowsPerPage,
    changed: rowsPerPageChanged,
    apply: applyRowsPerPage,
  } = useSavedField(settings.rowsPerPage);

  const getValidHour = (hour: number | null | undefined): number => {
    if(hour === null || hour === undefined) return DEFAULT_BUSINESS_START_HOUR;
    return Number.isNaN(hour) ? DEFAULT_BUSINESS_START_HOUR : hour;
  }

  const { mutate: updateCalendarPreferences } = useMutation({
    mutationFn: async (data: CalendarPreferences) => {
      const calendarResponse = await apiFetch<CalendarPreferences>(
        `org/organisation/${auth?.userData?.profile?.sub}/calendar`,
        data,
        'PUT',
        process.env.REACT_APP_API_V1,
        `Bearer ${maybeAccessToken}`
      );

      await retryOperation(
        () =>
          apiFetch(
            `/data/v1/notifications/preferences/business-hour`,
            { businessHour: data.daysStart },
            'PUT',
            process.env.REACT_APP_API_GATEWAY,
            `Bearer ${maybeAccessToken}`
          ),
        2,
        500
      );

      return {
        weeksStart: calendarResponse?.weeksStart,
        daysStart: getValidHour(calendarResponse?.daysStart)
      };
    },
    onSuccess: (data) => {
      forceSetDaysStart(() => new Date(2024, 1, 1, data.daysStart));
      applyDaysStart();
      applyWeeksStart();
      setSnackbar({
        severity: 'success',
        message: t('screens.settings.updatedSuccessfully'),
      });
      defer(() => setSnackbarOpen(true));
    },
    onError: () => {
      setSnackbar({
        severity: 'error',
        message: t('components.error.somethingFailed'),
      });
      defer(() => setSnackbarOpen(true));
    },
  });

  const saveSettings = useCallback(() => {
    updateCalendarPreferences({ daysStart: daysStart.getHours(), weeksStart: weeksStart || "monday" });
    setSetting('rowsPerPage', rowsPerPage);
    applyRowsPerPage();
  }, [updateCalendarPreferences, daysStart, weeksStart, setSetting, rowsPerPage, applyRowsPerPage]);

  useEffect(() => {
    if (!allowUpdate) return undefined;

    const hasChanges = daysStartChanged || weeksStartChanged || rowsPerPageChanged;
    if (!hasChanges) return undefined;

    const handler = setTimeout(saveSettings, 400);
    return () => clearTimeout(handler);
  }, [
    daysStartChanged,
    weeksStartChanged,
    rowsPerPageChanged,
    allowUpdate,
    saveSettings,
  ]);


  useEffect(() => {
    if (!calendarPreferences) return;
    const d = new Date();
    const hours = getValidHour(calendarPreferences.daysStart);
    d.setHours(hours);
    d.setMinutes(0);
    d.setSeconds(0);
    forceSetDaysStart(() => d);
    forceSetWeeksStart(() => calendarPreferences.weeksStart || "monday");
  }, [calendarPreferences]);

  return (
    <>
      <Box
        sx={{
          marginBottom: { xs: 0, sm: 3 },
          height: '4.75rem',
        }}
      >
        <Typography sx={{
          fontSize: '32px',
          fontWeight: 600,
          marginLeft: { xs: '16px', md: '32px' },
          marginBottom: { xs: '16px' },
          fontFamily: 'Poppins, sans-serif',
        }}>
          {t('screens.preferences.title')}
        </Typography>
      </Box>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginX: { xs: '16px', md: '32px' },

      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
          justifyContent: 'space-between',
        }}>
          {/* Business Schedule */}
          <Box sx={{ marginBottom: { xs: '24px' }, width: { xs: '100%', md: '30%' } }} >
            <Typography variant='h1' sx={{ marginBottom: '8px' }} >{t("screens.settings.dataTime")}</Typography>
            <Typography variant='body1' >{t("screens.settings.dataTimeDescription")}</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: { xs: '100%', md: '70%' }
            }}
          >
            <Card
              variant="outlined"
              sx={{ padding: "24px", overflow: "visible", flex: 1 }}
            >
              <Box>
                <Typography variant="h3" sx={{ marginBottom: '8px' }}>
                  {t("screens.settings.businessScheduleHeader")}
                </Typography>
                <Typography sx={subheaderStyle}>
                  {t("screens.settings.businessScheduleDescription")}
                </Typography>
              </Box>

              <Box sx={{ marginTop: "24px", display: "flex", gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                <Box sx={{ flex: 1, display: "flex" }}>
                  {isLoadingCalendarPreferences ? (
                    <Skeleton
                      sx={{ width: "260px", height: "72px", marginTop: "-5px" }}
                    />
                  ) : (
                    <TextField
                      select
                      inputProps={{
                        "data-testid": "weekStartsSelect",
                        "data-qa-id": "businessScheduleWeekStartsAt",
                      }}
                      value={weeksStart}
                      label={t("screens.settings.weekStartsOn")}
                      id="weeksStartOn"
                      sx={{ ...comboStyle, flexGrow: 1 }}
                      onChange={(ev) => setWeeksStart(ev.target.value as DayOfWeek)}
                    >
                      <MenuItem value="monday">{t("weekdays.monday")}</MenuItem>
                      <MenuItem value="tuesday">{t("weekdays.tuesday")}</MenuItem>
                      <MenuItem value="wednesday">{t("weekdays.wednesday")}</MenuItem>
                      <MenuItem value="thursday">{t("weekdays.thursday")}</MenuItem>
                      <MenuItem value="friday">{t("weekdays.friday")}</MenuItem>
                      <MenuItem value="saturday">{t("weekdays.saturday")}</MenuItem>
                      <MenuItem value="sunday">{t("weekdays.sunday")}</MenuItem>
                    </TextField>
                  )}
                </Box>

                <Box sx={{ flex: 1, minWidth: "50%", display: "flex" }}>
                  {isLoadingCalendarPreferences ? (
                    <Skeleton
                      sx={{ width: "260px", height: "72px", marginTop: "-5px" }}
                    />
                  ) : (
                    <TimePicker
                      ampm
                      minutesStep={60}
                      value={daysStart}
                      views={["hours"]}
                      onChange={(val) => setDaysStart(val || daysStart)}
                      format="hh a"
                      label={t("screens.settings.dayStartsAt")}
                      data-testId="daysStartAtPicker"
                      slotProps={{
                        textField: {
                          style: { ...comboStyle, flexGrow: 1 },
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <AccessTimeOutlinedIcon />
                              </InputAdornment>
                            ),
                          },
                          inputProps: {
                            "data-qa-id": "businessScheduleDayStartsAt",
                          },
                        },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Card>
          </Box>
        </Box>

        {/* Rows per Page */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
          justifyContent: 'space-between',
          marginTop: '40px',
          marginBottom: '16px'
        }}>
          <Box sx={{ marginBottom: { xs: '24px' }, width: { xs: '100%', md: '30%' } }}>
            <Typography variant='h1' sx={{ marginBottom: '8px' }} >{t("screens.settings.dataTablesHeader")}</Typography>
            <Typography variant='body1' >{t("screens.settings.dataTablesDescription")}</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: { xs: '100%', md: '70%' }
            }}
          >
            <Card variant="outlined" sx={{ padding: "24px", overflow: 'visible', flex: 1 }}>
              <Box>
                <Typography variant="h3">
                  {t("screens.settings.rowsHeader")}
                </Typography>
                <Typography sx={subheaderStyle}>
                  {t("screens.settings.rowsDescription")}
                </Typography>
              </Box>

              <Box sx={{ marginTop: "36px" }}>
                <TextField
                  fullWidth
                  select
                  inputProps={{
                    "data-testid": "rowsPerPageSelect",
                    "data-qa-id": "dataTablesRowsPerPageOption",
                  }}
                  value={rowsPerPage}
                  label={t("screens.settings.rowsPerPage")}
                  id="rowsPerPage"
                  sx={{ ...comboStyle }}
                  onChange={(ev) => setRowsPerPage(ev.target.value)}
                >
                  <MenuItem value="10">10</MenuItem>
                  <MenuItem value="25">25</MenuItem>
                  <MenuItem value="50">50</MenuItem>
                  <MenuItem value="100">100</MenuItem>
                </TextField>
              </Box>
            </Card>
          </Box>
        </Box>
      </Box>
      <EnhancedSnackbar open={snackbarOpen} severity={snackbar?.severity || "error"} message={snackbar?.message || ""}
        onClose={() => { setSnackbarOpen(false) }} forceColor={snackbar?.severity ? theme?.palette?.elevation?.['12dp'] : theme?.palette?.error?.['160p']}
      />
    </>
  );
};

export default Preferences;
